'use client'

import styled from '@emotion/styled'
import FacebookIcon from '@mui/icons-material/Facebook'
import TelegramIcon from '@mui/icons-material/Telegram'
import YouTubeIcon from '@mui/icons-material/YouTube'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import Link from '@mui/material/Link'
import Typography from '@mui/material/Typography'
import StoreBadges from '~/app/_components/badges'
import Logo from '~/app/_components/brand/logo'

const Wrapper = styled.div`
  position: relative;
  color: #d5dfff;
  background-color: #0a1929;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`

const FooterTitle = styled(Typography)`
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 16px;
  font-size: 0.875rem;
`

const FooterLink = styled(Link)`
  display: block;
  color: rgba(255, 255, 255, 0.5);
  text-decoration: none;
  font-size: 0.8125rem;
  padding: 3px 0;
  transition: color 0.2s;

  &:hover {
    color: #27b1c6;
  }
`

const SocialButton = styled(IconButton)`
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.5);
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #27b1c6;
  }
`

const platformLinks = [
  { label: 'Trading Platform', href: '/' },
  { label: 'Technology', href: '/technology' },
]

const companyLinks = [{ label: 'About Us', href: '/about-us' }]

const serviceLinks = [
  { label: 'Get Mobile App', href: '/get-app' },
  { label: 'Consultation', href: '/consultation' },
]

const legalLinks = [
  { label: 'Terms of Use', href: '/legal/terms-of-use' },
  { label: 'Privacy Policy', href: '/legal/privacy-policy' },
  { label: 'Customer Agreement (EU)', href: '/legal/customer-agreement-eu' },
  { label: 'Customer Agreement (US)', href: '/legal/customer-agreement-us' },
  { label: 'Payment Services Terms', href: '/legal/payment-services-terms' },
  { label: 'Acceptable Use Policy', href: '/legal/acceptable-use-policy' },
]

const complianceLinks = [
  { label: 'Risk Disclosure', href: '/legal/risk-disclosure' },
  { label: 'Regulatory Status', href: '/legal/regulatory-status-disclosure' },
  { label: 'MiCA CASP Readiness', href: '/legal/mica-casp-readiness' },
  { label: 'Conflicts of Interest', href: '/legal/conflicts-of-interest-policy' },
  { label: 'No Investment Advice', href: '/legal/no-investment-advice-disclaimer' },
  {
    label: 'Withdrawals & Complaints',
    href: '/legal/withdrawals-complaints-policy',
  },
  { label: 'Intellectual Property', href: '/legal/intellectual-property-notice' },
  { label: 'Group Legal Structure', href: '/legal/group-legal-structure' },
  { label: 'Marketers Disclosure', href: '/legal/independent-marketers-disclosure' },
]

const socialLinks = [
  { icon: TelegramIcon, href: 'https://t.me/polartensor', label: 'Telegram' },
  {
    icon: FacebookIcon,
    href: 'https://www.facebook.com/share/1A73y6QodY/',
    label: 'Facebook',
  },
  {
    icon: YouTubeIcon,
    href: 'https://youtube.com/@polartensor?si=-cOld_n9a0FLvue5',
    label: 'YouTube',
  },
]

function LinkColumn({
  title,
  links,
}: {
  title: string
  links: { label: string; href: string }[]
}) {
  return (
    <Box>
      <FooterTitle>{title}</FooterTitle>
      {links.map((link) => (
        <FooterLink key={link.label} href={link.href}>
          {link.label}
        </FooterLink>
      ))}
    </Box>
  )
}

export default function Footer() {
  return (
    <Wrapper>
      <Container maxWidth='lg' sx={{ py: 6 }}>
        <Grid container spacing={4} sx={{ mb: 4 }}>
          {/* Logo + tagline */}
          <Grid size={{ xs: 12, md: 3, lg: 2 }}>
            <Box sx={{ mb: 2 }}>
              <Logo color='#ffffff' width={60} />
            </Box>
            <Typography
              variant='body2'
              sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8125rem', mb: 2 }}
            >
              Transform your trading with AI-powered strategies.
            </Typography>
            <StoreBadges variant='dark' direction='column' />
          </Grid>

          {/* Platform */}
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <LinkColumn title='Platform' links={platformLinks} />
          </Grid>

          {/* Company */}
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <LinkColumn title='Company' links={companyLinks} />
          </Grid>

          {/* Services */}
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <LinkColumn title='Services' links={serviceLinks} />
          </Grid>

          {/* Legal */}
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <LinkColumn title='Legal' links={legalLinks} />
          </Grid>

          {/* Compliance */}
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <LinkColumn title='Compliance' links={complianceLinks} />
          </Grid>
        </Grid>

        {/* Bottom bar */}
        <Box
          sx={{
            borderTop: '1px solid rgba(255,255,255,0.1)',
            pt: 4,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography
            variant='body2'
            sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8125rem' }}
          >
            © {new Date().getFullYear()} Polar Tensor. All rights reserved.
          </Typography>

          <Box sx={{ display: 'flex', gap: 1 }}>
            {socialLinks.map((social) => (
              <Link
                key={social.label}
                href={social.href}
                target='_blank'
                rel='noopener noreferrer'
                aria-label={social.label}
                sx={{ textDecoration: 'none' }}
              >
                <SocialButton>
                  <social.icon sx={{ fontSize: 20 }} />
                </SocialButton>
              </Link>
            ))}
          </Box>
        </Box>
      </Container>
    </Wrapper>
  )
}
